import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useContract } from "../../hooks/useContract";
import Loader from "../../components/Loader/Loader";
import { formatToken, truncateAddress, formatDate } from "../../utils/format";
import { ACTIVITY_TYPES, PLAN_LABELS, PLAN1_PKG_LABELS, PLAN2_PKG_LABELS } from "../../utils/constants";
import { TOKEN_SYMBOL, PLAN1_PKG_COUNT, PLAN2_PKG_COUNT, NETWORK } from "../../config/config";
import { fetchActivityFromEvents } from "../../utils/activityEvents";
import { toast } from "react-toastify";
import "./Admin.css";

export default function Admin() {
  const { account, provider } = useWeb3();
  const contracts = useContract();
  const [isOwner, setIsOwner] = useState(false);
  const [isAdminOrOwner, setIsAdminOrOwner] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [tab, setTab] = useState(null);

  // Contract info
  const [info, setInfo] = useState({});
  // Royalty pool info
  const [poolInfo, setPoolInfo] = useState({ p1Royalty: null, p2Royalty: null });
  const [routerInfo, setRouterInfo] = useState({ p1: null, p2: null });

  // Royalty eligible users
  const [p1Eligible, setP1Eligible] = useState([]);
  const [p2Eligible, setP2Eligible] = useState([]);
  const [showP1Eligible, setShowP1Eligible] = useState(false);
  const [showP2Eligible, setShowP2Eligible] = useState(false);
  const [eligLoading, setEligLoading] = useState({ p1: false, p2: false });

  // Form states
  const [walletField, setWalletField] = useState({ type: "", address: "" });
  const [blacklistField, setBlacklistField] = useState({ uid: "", status: true });
  const [virtualRegField, setVirtualRegField] = useState({ wallet: "", refId: "", name: "" });
  const [virtualActField, setVirtualActField] = useState({ address: "", pkg: "0", plan: "1" });
  const [emergencyAmt, setEmergencyAmt] = useState("");
  const [routerField, setRouterField] = useState({ num: "1", enabled: true });
  const [routerPoolField, setRouterPoolField] = useState({ plan: "1", enabled: true });
  const [routerWithdrawField, setRouterWithdrawField] = useState({ plan: "1", amount: "" });
  const [royaltyField, setRoyaltyField] = useState({ plan: "1", amount: "" });
  const [globalShareField, setGlobalShareField] = useState({ pkg: "0", amount: "" });
  const [globalPools, setGlobalPools] = useState([]);
  const [adminChangeAddr, setAdminChangeAddr] = useState("");

  // Lookup user state
  const [lookupInput, setLookupInput] = useState("");
  const [lookupUser, setLookupUser] = useState(null);
  const [lookupP1, setLookupP1] = useState(null);
  const [lookupP2, setLookupP2] = useState(null);
  const [lookupP1Income, setLookupP1Income] = useState(null);
  const [lookupP2Income, setLookupP2Income] = useState(null);
  const [lookupActivity, setLookupActivity] = useState([]);
  const [lookupActTotal, setLookupActTotal] = useState(0);
  const [lookupActPage, setLookupActPage] = useState(0);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Members list
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    if (account && contracts) checkAdmin();
    // eslint-disable-next-line
  }, [account, contracts]);

  async function checkAdmin() {
    setPageLoading(true);
    try {
      const [owner, admin] = await Promise.all([
        contracts.data.owner(),
        contracts.data.admin(),
      ]);
      const lower = account.toLowerCase();
      const ownerMatch = lower === owner.toLowerCase();
      const adminMatch = lower === admin.toLowerCase();
      setIsOwner(ownerMatch);
      setIsAdminOrOwner(ownerMatch || adminMatch);
      if (ownerMatch || adminMatch) {
        setTab(ownerMatch ? "overview" : "lookup");
        await loadInfo();
      }
    } catch (err) {
      console.error("Admin check:", err);
    } finally {
      setPageLoading(false);
    }
  }

  async function loadInfo() {
    try {
      const [
        owner, admin, adminWallet, deductionWallet, missingWallet,
        router1, router2, r1Enabled, r2Enabled,
        balance, totalUsers, p1Addr, p2Addr,
      ] = await Promise.all([
        contracts.data.owner(), contracts.data.admin(),
        contracts.data.adminWallet(), contracts.data.deductionWallet(), contracts.data.missingIncomeWallet(),
        contracts.data.router1Wallet(), contracts.data.router2Wallet(),
        contracts.data.router1Enabled(), contracts.data.router2Enabled(),
        contracts.data.getContractBalance(), contracts.data.totalUsers(),
        contracts.data.plan1Contract(), contracts.data.plan2Contract(),
      ]);
      setInfo({
        owner, admin, adminWallet, deductionWallet, missingWallet,
        router1, router2, r1Enabled, r2Enabled,
        balance, totalUsers: totalUsers.toNumber(),
        plan1: p1Addr, plan2: p2Addr,
      });
      await loadPoolInfo();
    } catch (err) {
      console.error("loadInfo:", err);
    }
  }

  async function loadPoolInfo() {
    try {
      const [p1Royalty, p2Royalty, p1RPool, p2RPool, p1RPEnabled, p2RPEnabled] = await Promise.all([
        contracts.plan1.getRoyaltyInfo(),
        contracts.plan2.getRoyaltyInfo(),
        contracts.plan1.getRouterPoolInfo(),
        contracts.plan2.getRouterPoolInfo(),
        contracts.plan1.routerPoolEnabled(),
        contracts.plan2.routerPoolEnabled(),
      ]);
      setPoolInfo({
        p1Royalty: { pool: p1Royalty.pool, round: p1Royalty.round_, eligible: p1Royalty.eligible, dps: p1Royalty.dps_ },
        p2Royalty: { pool: p2Royalty.pool, round: p2Royalty.round_, eligible: p2Royalty.eligible, dps: p2Royalty.dps_ },
      });
      setRouterInfo({
        p1: { poolBalance: p1RPool, poolEnabled: p1RPEnabled },
        p2: { poolBalance: p2RPool, poolEnabled: p2RPEnabled },
      });

      // Global share pools for Plan2 (per package)
      const pools = await Promise.all(
        Array.from({ length: PLAN2_PKG_COUNT }, (_, i) => contracts.plan2.getGlobalPoolInfo(i).catch(() => null))
      );
      setGlobalPools(pools);
    } catch (err) {
      console.error("loadPoolInfo:", err);
    }
  }

  // ── Members ──
  async function loadEligibleUsers(planNum) {
    const planKey = planNum === 1 ? "p1" : "p2";
    setEligLoading((prev) => ({ ...prev, [planKey]: true }));
    try {
      const planContract = planNum === 1 ? contracts.plan1 : contracts.plan2;
      const PAGE = 200;
      let total = 0;
      let allIds = [];
      let page = 0;
      while (true) {
        const res = await contracts.data.getAllUsersNewest(page, PAGE);
        total = res.total.toNumber();
        allIds.push(...res.ids.map((id) => id.toNumber()).filter((id) => id > 0));
        page++;
        if (page * PAGE >= total) break;
        if (page > 20) break; // safety cap (max 4000 users scanned)
      }

      // Check eligibility in parallel batches of 30
      const eligible = [];
      for (let i = 0; i < allIds.length; i += 30) {
        const batch = allIds.slice(i, i + 30);
        const checks = await Promise.all(
          batch.map((uid) => planContract.royaltyEligible(uid).then((e) => ({ uid, e })).catch(() => ({ uid, e: false })))
        );
        eligible.push(...checks.filter((c) => c.e).map((c) => c.uid));
      }

      // Fetch user info (name + wallet) for eligible ones
      const details = [];
      for (let i = 0; i < eligible.length; i += 20) {
        const batch = eligible.slice(i, i + 20);
        const infos = await Promise.all(
          batch.map(async (uid) => {
            try {
              const info = await contracts.data.getUserInfo(uid);
              return { id: uid, name: info.name, wallet: info.wallet };
            } catch { return { id: uid, name: "", wallet: "" }; }
          })
        );
        details.push(...infos);
      }
      details.sort((a, b) => a.id - b.id);

      if (planNum === 1) setP1Eligible(details); else setP2Eligible(details);
    } catch (err) {
      console.error("loadEligibleUsers:", err);
      toast.error("Failed to load eligible users");
    } finally {
      setEligLoading((prev) => ({ ...prev, [planKey]: false }));
    }
  }

  async function toggleEligibleList(planNum) {
    if (planNum === 1) {
      if (!showP1Eligible && p1Eligible.length === 0) await loadEligibleUsers(1);
      setShowP1Eligible(!showP1Eligible);
    } else {
      if (!showP2Eligible && p2Eligible.length === 0) await loadEligibleUsers(2);
      setShowP2Eligible(!showP2Eligible);
    }
  }

  async function loadMembers(page) {
    setMembersLoading(true);
    try {
      const res = await contracts.data.getAllUsersNewest(page, 10);
      const ids = res.ids.map((id) => id.toNumber()).filter((id) => id > 0);
      const users = await Promise.all(ids.map(async (uid) => {
        try {
          const [info, p1Pkgs, p2Pkgs, acts] = await Promise.all([
            contracts.data.getUserInfo(uid),
            contracts.plan1.getAllPackages(uid),
            contracts.plan2.getAllPackages(uid),
            contracts.data.getActivity(uid, 0, 1),
          ]);
          const starPkgs = [];
          const globalPkgs = [];
          for (let p = 0; p < 8; p++) {
            if (p1Pkgs.a[p]) starPkgs.push(`$${[1,2,4,8,16,32,64,128][p]}`);
            if (p2Pkgs.a[p]) globalPkgs.push(`$${[1,2,4,8,16,32,64,128][p]}`);
          }
          // First activity timestamp as joined date (newest first, so last page = oldest, but we get first entry as latest)
          // Actually getActivity returns newest first. The total count tells us how many. The join date is the oldest entry.
          // For simplicity, use the first activity's total to know they joined, and show the latest activity date.
          let joinedTs = null;
          if (acts.acts.length > 0) {
            // Get the oldest activity by fetching the last page
            const total = acts.total.toNumber();
            if (total > 0) {
              const lastPage = Math.floor((total - 1) / 1);
              const oldest = await contracts.data.getActivity(uid, lastPage, 1);
              if (oldest.acts.length > 0) joinedTs = oldest.acts[0].timestamp;
            }
          }
          return { id: uid, name: info.name || "", wallet: info.wallet, active: info.active, invested: info.totalInvested, starPkgs, globalPkgs, joinedTs };
        } catch { return { id: uid, name: "", wallet: "", active: false, invested: null, starPkgs: [], globalPkgs: [], joinedTs: null }; }
      }));
      setMembers(users);
      setMembersTotal(res.total.toNumber());
      setMembersPage(page);
    } catch (err) { console.error("loadMembers:", err); }
    finally { setMembersLoading(false); }
  }

  // ── Lookup User ──
  async function handleLookup() {
    if (!lookupInput.trim()) { toast.error("Enter a user ID or wallet address"); return; }
    setLookupLoading(true);
    setLookupUser(null); setLookupP1(null); setLookupP2(null);
    setLookupP1Income(null); setLookupP2Income(null); setLookupActivity([]);
    try {
      let uid;
      const input = lookupInput.trim();
      if (input.startsWith("0x") && input.length === 42) {
        uid = (await contracts.data.getIdByAddress(input)).toNumber();
      } else {
        uid = parseInt(input);
      }
      if (!uid || uid === 0) { toast.error("User not found"); setLookupLoading(false); return; }

      const [userInfo, p1Pkgs, p2Pkgs, p1Inc, p2Inc] = await Promise.all([
        contracts.data.getUserInfo(uid),
        contracts.plan1.getAllPackages(uid),
        contracts.plan2.getAllPackages(uid),
        contracts.plan1.getIncomeStats(uid),
        contracts.plan2.getIncomeStats(uid),
      ]);
      setLookupUser({
        wallet: userInfo.wallet, id: userInfo.id.toNumber(), referrerId: userInfo.referrerId.toNumber(),
        name: userInfo.name, totalInvested: userInfo.totalInvested, totalEarned: userInfo.totalEarned,
        totalClaimed: userInfo.totalClaimed, claimable: userInfo.claimable,
        directRefCount: userInfo.directRefCount.toNumber(), active: userInfo.active, isBlacklisted: userInfo.isBL,
      });
      setLookupP1(p1Pkgs); setLookupP2(p2Pkgs);
      setLookupP1Income({ direct: p1Inc.direct, level: p1Inc.lvl, royalty: p1Inc.royalty });
      setLookupP2Income({ direct: p2Inc.direct, level: p2Inc.lvl, global: p2Inc.glob, royalty: p2Inc.royalty });
      await loadLookupActivity(uid, 0);
    } catch (err) {
      console.error("Lookup error:", err);
      toast.error("Failed to lookup user");
    } finally { setLookupLoading(false); }
  }

  async function loadLookupActivity(uid, page) {
    try {
      // Fetch fresh from events only on first page load (page 0)
      if (page === 0 || lookupActivity.length === 0) {
        const events = await fetchActivityFromEvents(contracts, provider, uid);
        setLookupActivity(events);
        setLookupActTotal(events.length);
      }
      setLookupActPage(page);
    } catch (err) { console.error("loadLookupActivity:", err); }
  }

  // ── Actions ──
  async function handleChangeAdmin() {
    if (!adminChangeAddr) { toast.error("Enter new admin address"); return; }
    setProcessing(true);
    try {
      toast.info("Updating admin on all 3 contracts...");
      const tx1 = await contracts.data.setAdmin(adminChangeAddr); await tx1.wait();
      const tx2 = await contracts.plan1.setAdmin(adminChangeAddr); await tx2.wait();
      const tx3 = await contracts.plan2.setAdmin(adminChangeAddr); await tx3.wait();
      toast.success("Admin updated on all contracts!");
      setAdminChangeAddr(""); await loadInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); }
    finally { setProcessing(false); }
  }

  async function handleSetWallet() {
    if (!walletField.type || !walletField.address) { toast.error("Select wallet type and enter address"); return; }
    setProcessing(true);
    try {
      const fn = { admin: "setAdminWallet", deduction: "setDeductionWallet", missing: "setMissingIncomeWallet", router1: "setRouter1", router2: "setRouter2" }[walletField.type];
      const tx = await contracts.data[fn](walletField.address); await tx.wait();
      toast.success("Wallet updated!"); setWalletField({ type: "", address: "" }); await loadInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleBlacklist() {
    if (!blacklistField.uid) { toast.error("Enter user ID"); return; }
    setProcessing(true);
    try {
      const tx = await contracts.data.blacklistUser(parseInt(blacklistField.uid), blacklistField.status); await tx.wait();
      toast.success(`User ${blacklistField.status ? "blacklisted" : "unblacklisted"}!`); setBlacklistField({ uid: "", status: true });
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleVirtualRegister() {
    if (!virtualRegField.wallet) { toast.error("Enter wallet address"); return; }
    if (!virtualRegField.name.trim()) { toast.error("Enter name"); return; }
    setProcessing(true);
    try {
      const tx = await contracts.data.virtualRegister(virtualRegField.wallet, parseInt(virtualRegField.refId) || 0, virtualRegField.name.trim()); await tx.wait();
      toast.success("Virtual registration successful!"); setVirtualRegField({ wallet: "", refId: "", name: "" });
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleVirtualActivate() {
    const { address, pkg, plan } = virtualActField;
    if (!address || pkg === "") { toast.error("Enter user ID or address, and select package"); return; }
    setProcessing(true);
    try {
      // Accept either user ID or wallet address
      let uid;
      const input = address.trim();
      if (input.startsWith("0x") && input.length === 42) {
        uid = (await contracts.data.getIdByAddress(input)).toNumber();
      } else {
        uid = parseInt(input);
      }
      if (!uid) { toast.error("User not found"); setProcessing(false); return; }

      const contract = plan === "1" ? contracts.plan1 : contracts.plan2;

      // virtualActivate is admin-only (no USDT, no income paid)
      try { await contract.callStatic.virtualActivate(uid, parseInt(pkg)); }
      catch (sim) { throw new Error(sim.reason || sim.errorName || sim.message || "Simulation failed"); }

      toast.info("Virtual activating...");
      const tx = await contract.virtualActivate(uid, parseInt(pkg)); await tx.wait();
      toast.success("Package virtually activated!");
      setVirtualActField({ address: "", pkg: "0", plan: "1" });
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleEmergencyWithdraw() {
    if (!emergencyAmt || parseFloat(emergencyAmt) <= 0) { toast.error("Enter a valid amount"); return; }
    setProcessing(true);
    try {
      const tx = await contracts.data.emergencyWithdraw(ethers.utils.parseUnits(emergencyAmt, 18)); await tx.wait();
      toast.success("Emergency withdraw successful!"); setEmergencyAmt(""); await loadInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleSetRouter() {
    setProcessing(true);
    try {
      const fn = routerField.num === "1" ? "setRouter1Enabled" : "setRouter2Enabled";
      const tx = await contracts.data[fn](routerField.enabled); await tx.wait();
      toast.success("Router status updated!"); await loadInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleSetRouterPoolEnabled() {
    setProcessing(true);
    try {
      const contract = routerPoolField.plan === "1" ? contracts.plan1 : contracts.plan2;
      const tx = await contract.setRouterPoolEnabled(routerPoolField.enabled); await tx.wait();
      toast.success(`${routerPoolField.plan === "1" ? "Star" : "Global"} router pool updated!`); await loadPoolInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleWithdrawRouterPool() {
    if (!routerWithdrawField.amount || parseFloat(routerWithdrawField.amount) <= 0) { toast.error("Enter amount"); return; }
    setProcessing(true);
    try {
      const amt = ethers.utils.parseUnits(routerWithdrawField.amount, 18);
      const contract = routerWithdrawField.plan === "1" ? contracts.plan1 : contracts.plan2;
      const tx = await contract.withdrawRouterPool(amt); await tx.wait();
      toast.success("Router pool withdrawn!"); setRouterWithdrawField({ ...routerWithdrawField, amount: "" }); await loadPoolInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleDistributeRoyalty() {
    if (!royaltyField.amount || parseFloat(royaltyField.amount) <= 0) { toast.error("Enter amount"); return; }
    setProcessing(true);
    try {
      const contract = royaltyField.plan === "1" ? contracts.plan1 : contracts.plan2;
      const tx = await contract.distributeRoyalty(ethers.utils.parseUnits(royaltyField.amount, 18)); await tx.wait();
      toast.success("Royalty distributed!"); setRoyaltyField({ ...royaltyField, amount: "" }); await loadPoolInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  async function handleDistributeGlobalShare() {
    if (!globalShareField.amount || parseFloat(globalShareField.amount) <= 0) { toast.error("Enter amount"); return; }
    setProcessing(true);
    try {
      const pkg = parseInt(globalShareField.pkg);
      const amt = ethers.utils.parseUnits(globalShareField.amount, 18);
      try { await contracts.plan2.callStatic.distributeGlobalShare(pkg, amt); }
      catch (sim) { throw new Error(sim.reason || sim.errorName || sim.message || "Simulation failed"); }
      const tx = await contracts.plan2.distributeGlobalShare(pkg, amt); await tx.wait();
      toast.success("Global share distributed!"); setGlobalShareField({ ...globalShareField, amount: "" }); await loadPoolInfo();
    } catch (err) { toast.error(err.reason || err.message || "Failed"); } finally { setProcessing(false); }
  }

  if (!account) return <Navigate to="/" replace />;
  if (pageLoading) return <Loader message="Checking admin access..." />;
  if (!isAdminOrOwner) {
    return (
      <div className="admin-page">
        <div className="admin-denied"><h2>Access Denied</h2><p>Only owner and admin can access this panel.</p></div>
      </div>
    );
  }

  const lookupActPages = Math.ceil(lookupActTotal / 10);

  // Compute per-user royalty amounts
  const p1PerUser = poolInfo.p1Royalty && poolInfo.p1Royalty.eligible.toNumber() > 0
    ? poolInfo.p1Royalty.pool.div(poolInfo.p1Royalty.eligible) : null;
  const p2PerUser = poolInfo.p2Royalty && poolInfo.p2Royalty.eligible.toNumber() > 0
    ? poolInfo.p2Royalty.pool.div(poolInfo.p2Royalty.eligible) : null;

  // Owner sees all tabs, admin sees limited tabs
  const OWNER_TABS = ["overview", "lookup", "members", "wallets", "blacklist", "virtual-reg", "virtual-act", "royalty", "global-share", "routers", "emergency"];
  const ADMIN_TABS = ["lookup", "members", "blacklist", "virtual-reg", "virtual-act", "royalty", "global-share", "emergency"];
  const TABS = isOwner ? OWNER_TABS : ADMIN_TABS;

  return (
    <div className="admin-page">
      <div className="dashboard-header">
        <div>
          <h2 className="page-title">Admin Panel</h2>
          <p className="page-sub">{isOwner ? "Owner" : "Admin"} - {info.totalUsers || 0} users - {formatToken(info.balance)} {TOKEN_SYMBOL}</p>
        </div>
        <button className="btn-neon btn-small" onClick={loadInfo}>Refresh</button>
      </div>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab-btn ${tab === t ? "tab-active" : ""}`} onClick={() => setTab(t)}>
            {t.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB (owner only) ── */}
      {tab === "overview" && isOwner && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Platform Overview</h3>
          <div className="admin-info-grid">
            <div className="admin-info-item"><span>Owner</span><span className="mono">{truncateAddress(info.owner)}</span></div>
            <div className="admin-info-item"><span>Admin</span><span className="mono">{truncateAddress(info.admin)}</span></div>
            <div className="admin-info-item"><span>Contract Balance</span><span>{formatToken(info.balance)} {TOKEN_SYMBOL}</span></div>
            <div className="admin-info-item"><span>Total Users</span><span>{info.totalUsers}</span></div>
            <div className="admin-info-item"><span>Plan 1 Contract</span><span className="mono">{truncateAddress(info.plan1)}</span></div>
            <div className="admin-info-item"><span>Plan 2 Contract</span><span className="mono">{truncateAddress(info.plan2)}</span></div>
            <div className="admin-info-item"><span>Admin Wallet</span><span className="mono">{truncateAddress(info.adminWallet)}</span></div>
            <div className="admin-info-item"><span>Deduction Wallet</span><span className="mono">{truncateAddress(info.deductionWallet)}</span></div>
            <div className="admin-info-item"><span>Missing Income Wallet</span><span className="mono">{truncateAddress(info.missingWallet)}</span></div>
            <div className="admin-info-item"><span>Router 1</span><span>{info.r1Enabled ? "On" : "Off"} - {truncateAddress(info.router1)}</span></div>
            <div className="admin-info-item"><span>Router 2</span><span>{info.r2Enabled ? "On" : "Off"} - {truncateAddress(info.router2)}</span></div>
          </div>
        </div>
      )}

      {/* ── LOOKUP TAB ── */}
      {tab === "lookup" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Lookup User</h3>
          <div className="lookup-search-row">
            <input className="form-input" placeholder="Enter user ID or wallet address (0x...)" value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLookup()} />
            <button className="btn-neon" onClick={handleLookup} disabled={lookupLoading}>{lookupLoading ? "Searching..." : "Search"}</button>
          </div>

          {lookupUser && (
            <div className="lookup-results">
              <div className="lookup-section">
                <h4 className="lookup-section-title">User Info</h4>
                <div className="admin-info-grid">
                  <div className="admin-info-item"><span>ID</span><span>{lookupUser.id}</span></div>
                  <div className="admin-info-item"><span>Name</span><span>{lookupUser.name || "-"}</span></div>
                  <div className="admin-info-item"><span>Wallet</span><span className="mono">{truncateAddress(lookupUser.wallet)}</span></div>
                  <div className="admin-info-item"><span>Sponsor ID</span><span>{lookupUser.referrerId}</span></div>
                  <div className="admin-info-item"><span>Direct Refs</span><span>{lookupUser.directRefCount}</span></div>
                  <div className="admin-info-item"><span>Status</span><span className={lookupUser.active ? "text-green" : "text-red"}>{lookupUser.active ? "Active" : "Inactive"}</span></div>
                  <div className="admin-info-item"><span>Blacklisted</span><span className={lookupUser.isBlacklisted ? "text-red" : "text-green"}>{lookupUser.isBlacklisted ? "Yes" : "No"}</span></div>
                  <div className="admin-info-item"><span>Total Invested</span><span>{formatToken(lookupUser.totalInvested)} {TOKEN_SYMBOL}</span></div>
                  <div className="admin-info-item"><span>Total Earned</span><span>{formatToken(lookupUser.totalEarned)} {TOKEN_SYMBOL}</span></div>
                  <div className="admin-info-item"><span>Total Claimed</span><span>{formatToken(lookupUser.totalClaimed)} {TOKEN_SYMBOL}</span></div>
                  <div className="admin-info-item"><span>Claimable</span><span>{formatToken(lookupUser.claimable)} {TOKEN_SYMBOL}</span></div>
                </div>
              </div>

              <div className="lookup-section">
                <h4 className="lookup-section-title">Income Summary</h4>
                <div className="lookup-income-grid">
                  <div className="lookup-income-card">
                    <h4 className="lookup-income-plan">Star Plan</h4>
                    <div className="lookup-inc-row"><span>Direct Ref</span><span>{lookupP1Income ? formatToken(lookupP1Income.direct) : "0.0000"}</span></div>
                    <div className="lookup-inc-row"><span>Level</span><span>{lookupP1Income ? formatToken(lookupP1Income.level) : "0.0000"}</span></div>
                    <div className="lookup-inc-row"><span>Royalty</span><span>{lookupP1Income ? formatToken(lookupP1Income.royalty) : "0.0000"}</span></div>
                  </div>
                  <div className="lookup-income-card">
                    <h4 className="lookup-income-plan lookup-income-plan-global">Global Plan</h4>
                    <div className="lookup-inc-row"><span>Direct Ref</span><span>{lookupP2Income ? formatToken(lookupP2Income.direct) : "0.0000"}</span></div>
                    <div className="lookup-inc-row"><span>Level</span><span>{lookupP2Income ? formatToken(lookupP2Income.level) : "0.0000"}</span></div>
                    <div className="lookup-inc-row"><span>Global Share</span><span>{lookupP2Income ? formatToken(lookupP2Income.global) : "0.0000"}</span></div>
                    <div className="lookup-inc-row"><span>Royalty</span><span>{lookupP2Income ? formatToken(lookupP2Income.royalty) : "0.0000"}</span></div>
                  </div>
                </div>
              </div>

              <div className="lookup-section">
                <h4 className="lookup-section-title">Star Packages</h4>
                <div className="lookup-pkg-grid">
                  {Array.from({ length: PLAN1_PKG_COUNT }, (_, i) => (
                    <div key={i} className={`lookup-pkg-item ${lookupP1 && lookupP1.a[i] ? "lookup-pkg-active" : ""}`}>
                      <span className="lookup-pkg-name">{PLAN1_PKG_LABELS[i]}</span>
                      <span className={`lookup-pkg-status ${lookupP1 && lookupP1.a[i] ? "text-green" : "text-dim"}`}>{lookupP1 && lookupP1.a[i] ? "Active" : "Inactive"}</span>
                      {lookupP1 && lookupP1.a[i] && <span className="lookup-pkg-earned">Earned: {formatToken(lookupP1.e[i])} {TOKEN_SYMBOL}</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="lookup-section">
                <h4 className="lookup-section-title">Global Packages</h4>
                <div className="lookup-pkg-grid">
                  {Array.from({ length: PLAN2_PKG_COUNT }, (_, i) => (
                    <div key={i} className={`lookup-pkg-item ${lookupP2 && lookupP2.a[i] ? "lookup-pkg-active" : ""}`}>
                      <span className="lookup-pkg-name">{PLAN2_PKG_LABELS[i]}</span>
                      <span className={`lookup-pkg-status ${lookupP2 && lookupP2.a[i] ? "text-green" : "text-dim"}`}>{lookupP2 && lookupP2.a[i] ? "Active" : "Inactive"}</span>
                      {lookupP2 && lookupP2.a[i] && <span className="lookup-pkg-earned">Earned: {formatToken(lookupP2.e[i])} {TOKEN_SYMBOL}</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="lookup-section">
                <h4 className="lookup-section-title">Activity History ({lookupActTotal} total)</h4>
                <div className="lookup-activity-list">
                  {lookupActivity.length === 0 ? (
                    <div className="team-empty">No activity</div>
                  ) : lookupActivity.slice(lookupActPage * 10, (lookupActPage + 1) * 10).map((a, i) => (
                    <div key={`${a.txHash}-${a.logIndex}`} className="lookup-activity-row">
                      <span className="lookup-act-type">{ACTIVITY_TYPES[a.actType] || `Type ${a.actType}`}</span>
                      <span className="lookup-act-plan">{PLAN_LABELS[a.plan] || ""}</span>
                      {(a.actType === 3 || a.actType === 4) && <span className="lookup-act-level">L{a.level + 1}</span>}
                      {a.fromUserId > 0 && <span className="lookup-act-from">#{a.fromUserId}</span>}
                      <span className="lookup-act-amount">{formatToken(a.amount)} {TOKEN_SYMBOL}</span>
                      <span className="lookup-act-time">{formatDate(a.timestamp)}</span>
                    </div>
                  ))}
                </div>
                {lookupActPages > 1 && (
                  <div className="team-pagination">
                    <button className="page-btn" disabled={lookupActPage === 0} onClick={() => setLookupActPage(lookupActPage - 1)}>Prev</button>
                    <span className="page-info">Page {lookupActPage + 1} of {lookupActPages}</span>
                    <button className="page-btn" disabled={lookupActPage >= lookupActPages - 1} onClick={() => setLookupActPage(lookupActPage + 1)}>Next</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── WALLETS TAB (owner only) ── */}
      {tab === "wallets" && isOwner && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Set Wallet Address</h3>
          <div className="form-group">
            <label className="form-label">Wallet Type</label>
            <select className="form-input" value={walletField.type} onChange={(e) => setWalletField({ ...walletField, type: e.target.value })}>
              <option value="">Select...</option>
              <option value="admin">Admin Wallet</option>
              <option value="deduction">Deduction Wallet</option>
              <option value="missing">Missing Income Wallet</option>
              <option value="router1">Router 1 Wallet</option>
              <option value="router2">Router 2 Wallet</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" placeholder="0x..." value={walletField.address} onChange={(e) => setWalletField({ ...walletField, address: e.target.value })} />
          </div>
          <button className="btn-neon" onClick={handleSetWallet} disabled={processing}>{processing ? "Processing..." : "Update Wallet"}</button>

          <div className="admin-divider" />

          <h3 className="admin-form-title">Change Admin Address (All 3 Contracts)</h3>
          <p className="admin-warning">This will update the admin on Data, Plan 1, and Plan 2 contracts.</p>
          <div className="form-group">
            <label className="form-label">New Admin Address</label>
            <input className="form-input" placeholder="0x..." value={adminChangeAddr} onChange={(e) => setAdminChangeAddr(e.target.value)} />
          </div>
          <button className="btn-neon btn-danger" onClick={handleChangeAdmin} disabled={processing}>{processing ? "Processing..." : "Change Admin"}</button>
        </div>
      )}

      {/* ── BLACKLIST TAB ── */}
      {tab === "blacklist" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Blacklist User</h3>
          <div className="form-group">
            <label className="form-label">User ID</label>
            <input className="form-input" placeholder="Enter user ID" value={blacklistField.uid} onChange={(e) => setBlacklistField({ ...blacklistField, uid: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Action</label>
            <select className="form-input" value={blacklistField.status} onChange={(e) => setBlacklistField({ ...blacklistField, status: e.target.value === "true" })}>
              <option value="true">Blacklist</option>
              <option value="false">Remove Blacklist</option>
            </select>
          </div>
          <button className="btn-neon" onClick={handleBlacklist} disabled={processing}>{processing ? "Processing..." : "Update Blacklist"}</button>
        </div>
      )}

      {/* ── VIRTUAL REG TAB ── */}
      {tab === "virtual-reg" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Virtual Register</h3>
          <div className="form-group">
            <label className="form-label">Wallet Address</label>
            <input className="form-input" placeholder="0x..." value={virtualRegField.wallet} onChange={(e) => setVirtualRegField({ ...virtualRegField, wallet: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" placeholder="User name" value={virtualRegField.name} onChange={(e) => setVirtualRegField({ ...virtualRegField, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Sponsor ID</label>
            <input className="form-input" placeholder="Sponsor ID (optional)" value={virtualRegField.refId} onChange={(e) => setVirtualRegField({ ...virtualRegField, refId: e.target.value })} />
          </div>
          <button className="btn-neon" onClick={handleVirtualRegister} disabled={processing}>{processing ? "Processing..." : "Register User"}</button>
        </div>
      )}

      {/* ── MEMBERS TAB ── */}
      {tab === "members" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">All Members ({membersTotal || info.totalUsers || 0})</h3>
          {members.length === 0 && !membersLoading && (
            <button className="btn-neon" onClick={() => loadMembers(0)}>Load Members</button>
          )}
          {membersLoading && <div className="mt-loading">Loading...</div>}
          {members.length > 0 && (
            <>
              <div className="members-list">
                {members.map((u) => (
                  <div key={u.id} className="member-card">
                    <div className="member-card-top">
                      <span className="member-id">#{u.id}</span>
                      <span className="member-name">{u.name || "-"}</span>
                      <span className={u.active ? "text-green" : "text-red"}>{u.active ? "Active" : "Inactive"}</span>
                    </div>
                    <div className="member-card-row">
                      <span className="member-wallet">{truncateAddress(u.wallet)}</span>
                      <span className="member-inv">{u.invested ? formatToken(u.invested) : "0"} {TOKEN_SYMBOL}</span>
                    </div>
                    {(u.starPkgs.length > 0 || u.globalPkgs.length > 0) && (
                      <div className="member-pkgs">
                        {u.starPkgs.length > 0 && (
                          <div className="member-pkg-row">
                            <span className="member-pkg-label">Star:</span>
                            {u.starPkgs.map((p, i) => <span key={i} className="member-pkg-badge member-pkg-star">{p}</span>)}
                          </div>
                        )}
                        {u.globalPkgs.length > 0 && (
                          <div className="member-pkg-row">
                            <span className="member-pkg-label">Global:</span>
                            {u.globalPkgs.map((p, i) => <span key={i} className="member-pkg-badge member-pkg-global">{p}</span>)}
                          </div>
                        )}
                      </div>
                    )}
                    {u.joinedTs && <div className="member-joined">Joined: {formatDate(u.joinedTs)}</div>}
                  </div>
                ))}
              </div>
              {Math.ceil(membersTotal / 10) > 1 && (
                <div className="team-pagination">
                  <button className="page-btn" disabled={membersPage === 0} onClick={() => loadMembers(membersPage - 1)}>Prev</button>
                  <span className="page-info">Page {membersPage + 1} of {Math.ceil(membersTotal / 10)}</span>
                  <button className="page-btn" disabled={membersPage >= Math.ceil(membersTotal / 10) - 1} onClick={() => loadMembers(membersPage + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── VIRTUAL ACT TAB ── */}
      {tab === "virtual-act" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Virtual Activate</h3>
          <p className="va-free-note">
            <span className="va-free-badge">FREE</span>
            Admin-only activation. No USDT required. No income paid out. Only sets the package as active.
          </p>
          <div className="form-group">
            <label className="form-label">User ID or Address</label>
            <input className="form-input" placeholder="User ID (e.g. 1234) or 0x..." value={virtualActField.address} onChange={(e) => setVirtualActField({ ...virtualActField, address: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-input" value={virtualActField.plan} onChange={(e) => setVirtualActField({ ...virtualActField, plan: e.target.value, pkg: "0" })}>
              <option value="1">Star Plan</option>
              <option value="2">Global Plan</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Package</label>
            <select className="form-input" value={virtualActField.pkg} onChange={(e) => setVirtualActField({ ...virtualActField, pkg: e.target.value })}>
              {Array.from({ length: virtualActField.plan === "1" ? PLAN1_PKG_COUNT : PLAN2_PKG_COUNT }, (_, i) => (
                <option key={i} value={i}>{(virtualActField.plan === "1" ? PLAN1_PKG_LABELS : PLAN2_PKG_LABELS)[i]}</option>
              ))}
            </select>
          </div>
          <button className="btn-neon" onClick={handleVirtualActivate} disabled={processing}>{processing ? "Processing..." : "Activate Package"}</button>
        </div>
      )}

      {/* ── ROYALTY TAB ── */}
      {tab === "royalty" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Royalty Pools</h3>

          <div className="royalty-pools-grid">
            <div className="royalty-pool-card">
              <h4 className="royalty-pool-plan">Star Plan</h4>
              <div className="royalty-pool-stats">
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Accumulated</span>
                  <span className="royalty-stat-value">{poolInfo.p1Royalty ? formatToken(poolInfo.p1Royalty.pool) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Eligible Users</span>
                  <span className="royalty-stat-value">{poolInfo.p1Royalty ? poolInfo.p1Royalty.eligible.toNumber() : 0}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Per User</span>
                  <span className="royalty-stat-value">{p1PerUser ? formatToken(p1PerUser) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Round</span>
                  <span className="royalty-stat-value">{poolInfo.p1Royalty ? poolInfo.p1Royalty.round.toNumber() : 0}</span>
                </div>
              </div>
            </div>

            <div className="royalty-pool-card royalty-pool-global">
              <h4 className="royalty-pool-plan">Global Plan</h4>
              <div className="royalty-pool-stats">
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Accumulated</span>
                  <span className="royalty-stat-value">{poolInfo.p2Royalty ? formatToken(poolInfo.p2Royalty.pool) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Eligible Users</span>
                  <span className="royalty-stat-value">{poolInfo.p2Royalty ? poolInfo.p2Royalty.eligible.toNumber() : 0}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Per User</span>
                  <span className="royalty-stat-value">{p2PerUser ? formatToken(p2PerUser) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Round</span>
                  <span className="royalty-stat-value">{poolInfo.p2Royalty ? poolInfo.p2Royalty.round.toNumber() : 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-divider" />

          {/* Eligible Users Lists */}
          <h3 className="admin-form-title">Royalty Eligible Users</h3>
          <div className="elig-grid">
            <div className="elig-card">
              <div className="elig-card-head">
                <span className="elig-card-title">Star Plan</span>
                <span className="elig-card-count">{poolInfo.p1Royalty ? poolInfo.p1Royalty.eligible.toNumber() : 0}</span>
              </div>
              <button className="btn-neon btn-small" onClick={() => toggleEligibleList(1)} disabled={eligLoading.p1}>
                {eligLoading.p1 ? "Loading..." : showP1Eligible ? "Hide List" : "Show Eligible Users"}
              </button>
              {showP1Eligible && (
                <div className="elig-list">
                  {p1Eligible.length === 0 ? (
                    <div className="elig-empty">No eligible users</div>
                  ) : (
                    p1Eligible.map((u) => (
                      <div key={u.id} className="elig-item">
                        <span className="elig-id">#{u.id}</span>
                        <span className="elig-name">{u.name || "User"}</span>
                        <span className="elig-wallet">{truncateAddress(u.wallet)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="elig-card elig-card-global">
              <div className="elig-card-head">
                <span className="elig-card-title">Global Plan</span>
                <span className="elig-card-count">{poolInfo.p2Royalty ? poolInfo.p2Royalty.eligible.toNumber() : 0}</span>
              </div>
              <button className="btn-neon btn-small" onClick={() => toggleEligibleList(2)} disabled={eligLoading.p2}>
                {eligLoading.p2 ? "Loading..." : showP2Eligible ? "Hide List" : "Show Eligible Users"}
              </button>
              {showP2Eligible && (
                <div className="elig-list">
                  {p2Eligible.length === 0 ? (
                    <div className="elig-empty">No eligible users</div>
                  ) : (
                    p2Eligible.map((u) => (
                      <div key={u.id} className="elig-item">
                        <span className="elig-id">#{u.id}</span>
                        <span className="elig-name">{u.name || "User"}</span>
                        <span className="elig-wallet">{truncateAddress(u.wallet)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="admin-divider" />

          <h3 className="admin-form-title">Distribute Royalty</h3>
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-input" value={royaltyField.plan} onChange={(e) => setRoyaltyField({ ...royaltyField, plan: e.target.value })}>
              <option value="1">Star Plan</option>
              <option value="2">Global Plan</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Amount ({TOKEN_SYMBOL})</label>
            <input className="form-input" type="number" placeholder="Amount to distribute" value={royaltyField.amount} onChange={(e) => setRoyaltyField({ ...royaltyField, amount: e.target.value })} step="0.01" min="0" />
          </div>
          <button className="btn-neon btn-secondary" onClick={handleDistributeRoyalty} disabled={processing}>{processing ? "Processing..." : "Distribute Royalty"}</button>
        </div>
      )}

      {/* ── GLOBAL SHARE TAB ── */}
      {tab === "global-share" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Global Share Pools (Plan 2)</h3>
          <div className="royalty-pools-grid">
            {globalPools.map((p, i) => p && (
              <div key={i} className="royalty-pool-card royalty-pool-global">
                <h4 className="royalty-pool-plan">{PLAN2_PKG_LABELS[i]}</h4>
                <div className="royalty-pool-stats">
                  <div className="royalty-stat">
                    <span className="royalty-stat-label">Pool Balance</span>
                    <span className="royalty-stat-value">{formatToken(p.pool)} {TOKEN_SYMBOL}</span>
                  </div>
                  <div className="royalty-stat">
                    <span className="royalty-stat-label">Eligible Users</span>
                    <span className="royalty-stat-value">{p.eligible.toNumber()}</span>
                  </div>
                  <div className="royalty-stat">
                    <span className="royalty-stat-label">Per User</span>
                    <span className="royalty-stat-value">{p.eligible.toNumber() > 0 ? formatToken(p.pool.div(p.eligible)) : "0.0000"} {TOKEN_SYMBOL}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="admin-divider" />

          <h3 className="admin-form-title">Distribute Global Share</h3>
          <div className="form-group">
            <label className="form-label">Package</label>
            <select className="form-input" value={globalShareField.pkg} onChange={(e) => setGlobalShareField({ ...globalShareField, pkg: e.target.value })}>
              {Array.from({ length: PLAN2_PKG_COUNT }, (_, i) => (
                <option key={i} value={i}>{PLAN2_PKG_LABELS[i]}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Amount ({TOKEN_SYMBOL})</label>
            <input className="form-input" type="number" placeholder="Amount to distribute" value={globalShareField.amount} onChange={(e) => setGlobalShareField({ ...globalShareField, amount: e.target.value })} step="0.01" min="0" />
          </div>
          <button className="btn-neon btn-secondary" onClick={handleDistributeGlobalShare} disabled={processing}>{processing ? "Processing..." : "Distribute Global Share"}</button>
        </div>
      )}

      {/* ── ROUTERS TAB (owner only) ── */}
      {tab === "routers" && isOwner && (
        <div className="admin-form-card">
          {/* Router Wallet Enable/Disable (shared in Data contract) */}
          <h3 className="admin-form-title">Router Wallets (Shared)</h3>
          <div className="admin-info-grid">
            <div className="admin-info-item">
              <span>Router 1</span>
              <span>{info.r1Enabled ? <span className="text-green">Enabled</span> : <span className="text-red">Disabled</span>} - {truncateAddress(info.router1)}</span>
            </div>
            <div className="admin-info-item">
              <span>Router 2</span>
              <span>{info.r2Enabled ? <span className="text-green">Enabled</span> : <span className="text-red">Disabled</span>} - {truncateAddress(info.router2)}</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Router</label>
            <select className="form-input" value={routerField.num} onChange={(e) => setRouterField({ ...routerField, num: e.target.value })}>
              <option value="1">Router 1</option>
              <option value="2">Router 2</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={routerField.enabled} onChange={(e) => setRouterField({ ...routerField, enabled: e.target.value === "true" })}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
          <button className="btn-neon" onClick={handleSetRouter} disabled={processing}>{processing ? "Processing..." : "Update Router"}</button>

          <div className="admin-divider" />

          {/* Router Pool Details (separate per plan) */}
          <h3 className="admin-form-title">Router Pools (Per Plan)</h3>
          <div className="royalty-pools-grid">
            <div className="royalty-pool-card">
              <h4 className="royalty-pool-plan">Star Plan Pool</h4>
              <div className="royalty-pool-stats">
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Balance</span>
                  <span className="royalty-stat-value">{routerInfo.p1 ? formatToken(routerInfo.p1.poolBalance) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Status</span>
                  <span className="royalty-stat-value">{routerInfo.p1 && routerInfo.p1.poolEnabled ? <span className="text-green">Enabled</span> : <span className="text-red">Disabled</span>}</span>
                </div>
              </div>
            </div>
            <div className="royalty-pool-card royalty-pool-global">
              <h4 className="royalty-pool-plan">Global Plan Pool</h4>
              <div className="royalty-pool-stats">
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Balance</span>
                  <span className="royalty-stat-value">{routerInfo.p2 ? formatToken(routerInfo.p2.poolBalance) : "0.0000"} {TOKEN_SYMBOL}</span>
                </div>
                <div className="royalty-stat">
                  <span className="royalty-stat-label">Status</span>
                  <span className="royalty-stat-value">{routerInfo.p2 && routerInfo.p2.poolEnabled ? <span className="text-green">Enabled</span> : <span className="text-red">Disabled</span>}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-divider" />

          {/* Enable/Disable Router Pool per plan */}
          <h3 className="admin-form-title">Router Pool Enable/Disable</h3>
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-input" value={routerPoolField.plan} onChange={(e) => setRouterPoolField({ ...routerPoolField, plan: e.target.value })}>
              <option value="1">Star Plan</option>
              <option value="2">Global Plan</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={routerPoolField.enabled} onChange={(e) => setRouterPoolField({ ...routerPoolField, enabled: e.target.value === "true" })}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
          <button className="btn-neon" onClick={handleSetRouterPoolEnabled} disabled={processing}>{processing ? "Processing..." : "Update Pool Status"}</button>

          <div className="admin-divider" />

          {/* Withdraw Router Pool */}
          <h3 className="admin-form-title">Withdraw Router Pool</h3>
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-input" value={routerWithdrawField.plan} onChange={(e) => setRouterWithdrawField({ ...routerWithdrawField, plan: e.target.value })}>
              <option value="1">Star Plan</option>
              <option value="2">Global Plan</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Amount ({TOKEN_SYMBOL})</label>
            <input className="form-input" type="number" placeholder="Amount to withdraw" value={routerWithdrawField.amount} onChange={(e) => setRouterWithdrawField({ ...routerWithdrawField, amount: e.target.value })} step="0.01" min="0" />
          </div>
          <button className="btn-neon" onClick={handleWithdrawRouterPool} disabled={processing}>{processing ? "Processing..." : "Withdraw"}</button>
        </div>
      )}

      {/* ── EMERGENCY TAB (owner only) ── */}
      {tab === "emergency" && (
        <div className="admin-form-card">
          <h3 className="admin-form-title">Emergency Withdraw</h3>
          <p className="admin-warning">This will withdraw tokens from the data contract. Use with caution.</p>
          <div className="form-group">
            <label className="form-label">Amount ({TOKEN_SYMBOL})</label>
            <input className="form-input" type="number" placeholder="Amount to withdraw" value={emergencyAmt} onChange={(e) => setEmergencyAmt(e.target.value)} step="0.01" min="0" />
          </div>
          <button className="btn-neon btn-danger" onClick={handleEmergencyWithdraw} disabled={processing}>{processing ? "Processing..." : "Emergency Withdraw"}</button>
        </div>
      )}
    </div>
  );
}
